import {
  PlaidLinkError,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOptions,
  usePlaidLink,
} from "react-plaid-link";
import { useNavigate } from "react-router-dom";

import { BankLogo } from "@assets";
import { ErrorComponent } from "@components/ErrorComponent";
import { ListItem } from "@components/ListItem";
import { Loader } from "@components/Loader";
import { MainLayout, MainLayoutProps } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import { AnalyticsEvent } from "@ctypes";
import {
  useBankLinkToken,
  useContributionFlow,
  useErrorBanner,
  useProcessBankLinkToken,
  useUser,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import { Analytics } from "@services";
import { useStyles } from "./styles";
import { ContributionBankAccountRouteProps } from "./types";

export const ContributionBankAccountRoute = () => {
  const { token, loading, error } = useBankLinkToken();

  if (loading) return <Loader />;
  if (error || !token) return <ErrorComponent />;

  return <ContributionBankAccountRouteComponent token={token} />;
};

const ContributionBankAccountRouteComponent = ({ token }: ContributionBankAccountRouteProps) => {
  const styles = useStyles();

  const navigate = useNavigate();

  const { bankAccounts, loading, error, refetch } = useUser();
  const [processPublicToken] = useProcessBankLinkToken();

  const { showErrorBanner } = useErrorBanner();

  const { setContributionProps } = useContributionFlow();

  const handleSuccess = async (
    publicToken: string | null,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => {
    const institutionId = metadata.institution?.institution_id;

    if (!institutionId || !publicToken) return showErrorBanner();

    try {
      await processPublicToken({
        variables: {
          institutionId,
          publicToken,
        },
      });
    } catch {
    } finally {
      refetch();
      exit();
    }
  };

  const handleExit = (err: PlaidLinkError | null) => {
    if (err?.error_code) {
      Analytics.track(AnalyticsEvent.BankLinkingExit, {
        errorCode: err.error_code,
      });

      showErrorBanner();
    }
    exit();
  };

  const config: PlaidLinkOptions = {
    onSuccess: handleSuccess,
    onExit: handleExit,
    token,
  };

  const { open, ready, exit } = usePlaidLink(config);

  const props: MainLayoutProps = {
    pageHeader: i18n.t(Translations.ContributionBankAccountRouteHeader),
  };

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  return (
    <MainLayout {...props}>
      <Body style={styles.text}>{i18n.t(Translations.ContributionBankAccountRouteTitle)}</Body>
      <Spacing size={SpacingSizes.M} />

      {bankAccounts?.map((account) => {
        const bankAccountId = account.id;

        if (!bankAccountId) return null;

        const onClick = () => {
          setContributionProps({ bankAccountId });
          navigate(-1);
        };

        return (
          <>
            <ListItem
              title={account?.name ?? account?.accountNumber ?? ""}
              leftIcon={<BankLogo />}
              onClick={onClick}
            />
            <Spacing size={SpacingSizes.S} />
          </>
        );
      })}

      <ListItem
        title={i18n.t(Translations.LinkedAccountsAddAccountTitle)}
        leftIcon={<BankLogo />}
        onClick={() => {
          if (ready) {
            open();
          }
        }}
      />
    </MainLayout>
  );
};
